/**
 * Logo de Brillamos Juntos ("Shine Together") por idioma. Mismo patrón que
 * `FundamentalsView/fundamentalsLogo.ts`: el arte llega rotulado por idioma, no como texto
 * traducible, así que hay un archivo por idioma en vez de una sola imagen + i18n.
 *
 * QA (2026-08-27) — solo existen cinco versiones (es/en/fr/pt/ro), en dos variantes de color
 * cada una. Alemán y neerlandés caen a inglés, el respaldo acordado (igual que Fundamentales).
 * Ver DT-029 en DEUDA_TECNICA.md: esta es la ÚNICA pantalla que consume el logo por idioma —
 * los otros 5 usos hardcodeados de un logo de Brillamos (PerfilView.tsx, postitShared.tsx) se
 * dejan pendientes a propósito, fuera del alcance de este fix.
 */

import { asset } from '@/utils/basePath';
type ShineLogoVariant = 'color' | 'white';

const LOGO_BY_LANGUAGE: Record<ShineLogoVariant, Record<string, string>> = {
  color: {
    es: asset('/brillamos/logo-shine-es.svg'),
    en: asset('/brillamos/logo-shine-en.svg'),
    fr: asset('/brillamos/logo-shine-fr.svg'),
    pt: asset('/brillamos/logo-shine-pt.svg'),
    ro: asset('/brillamos/logo-shine-ro.svg'),
  },
  white: {
    es: asset('/brillamos/logo-shine-es-white.svg'),
    en: asset('/brillamos/logo-shine-en-white.svg'),
    fr: asset('/brillamos/logo-shine-fr-white.svg'),
    pt: asset('/brillamos/logo-shine-pt-white.svg'),
    ro: asset('/brillamos/logo-shine-ro-white.svg'),
  },
};

/**
 * Ruta del logo de Brillamos Juntos para el idioma y variante dados. Acepta tanto códigos
 * cortos (`es`) como completos (`es-MX`), y responde con el logo en inglés para cualquier
 * idioma sin arte propio. `variant` por defecto es `'white'` — la que ya usaba el fallback
 * fijo que este helper reemplaza (`shine-hero-banner` es un fondo oscuro).
 */
export function shineLogoSrc(lang: string, variant: ShineLogoVariant = 'white'): string {
  const shortCode = lang.split('-')[0].toLowerCase();
  const byVariant = LOGO_BY_LANGUAGE[variant];
  return byVariant[shortCode] ?? byVariant['en'];
}
